import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Line } from "react-chartjs-2"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js"
import API from "../../common/API"
import AdminActivityLogUserDetail from "./AdminActivityLogUserDetail"

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler)

interface Props {
    organizationId: string
    organizationName: string
    onBack: () => void
}

interface TopUser {
    userId: string
    userName: string
    totalActions: number
    modules: number
}

interface SummaryData {
    entityName?: string
    entityEmail?: string
    entityImage?: string
    totalActions: number
    modulesUsed: number
    uniqueIps: number
    firstActivity?: string
    lastActivity?: string
    dailyTrend: { date: string; count: number }[]
    moduleBreakdown: { module: string; count: number }[]
    actionBreakdown: { action: string; count: number }[]
    topUsers?: TopUser[]
}

interface ActivityLogItem {
    id: string
    date?: string
    userId?: string
    userName?: string
    section?: string
    action?: string
    itemId?: string
    content?: string
    ipAddress?: string
}

const PAGE_SIZE = 20

const moduleColors = [
    "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6", "#f97316",
    "#ec4899", "#22c55e", "#eab308", "#6366f1", "#06b6d4",
]

const AdminActivityLogOrgDetail = ({ organizationId, organizationName, onBack }: Props) => {
    const { t } = useTranslation("global")
    const [summary, setSummary] = useState<SummaryData | null>(null)
    const [records, setRecords] = useState<ActivityLogItem[]>([])
    const [isLoadingSummary, setIsLoadingSummary] = useState(false)
    const [isLoadingLogs, setIsLoadingLogs] = useState(false)
    const [days, setDays] = useState(30)
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)
    const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string } | null>(null)

    const loadSummary = useCallback(async () => {
        try {
            setIsLoadingSummary(true)
            const res = await API.getAction(`ActivityLog/Summary?OrganizationId=${organizationId}&Days=${days}`)
            if (res.data.status === "ok") setSummary(res.data.response)
        } catch (ex) {
            console.error(ex)
        } finally {
            setIsLoadingSummary(false)
        }
    }, [organizationId, days])

    const loadLogs = useCallback(async () => {
        try {
            setIsLoadingLogs(true)
            const params = new URLSearchParams()
            params.set("Page", String(page))
            params.set("Quantity", String(PAGE_SIZE))
            params.set("OrganizationId", organizationId)
            const res = await API.getAction(`ActivityLog/List?${params.toString()}`)
            if (res.data.status === "ok") {
                setRecords(res.data.response || [])
                setTotalPages(res.data.quantityOfPages || 0)
                setTotalRecords(res.data.quantityOfRecords || 0)
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setIsLoadingLogs(false)
        }
    }, [organizationId, page])

    useEffect(() => { loadSummary() }, [loadSummary])
    useEffect(() => { loadLogs() }, [loadLogs])

    // Sub-navigation: user detail within org
    if (selectedUser) {
        return (
            <AdminActivityLogUserDetail
                userId={selectedUser.userId}
                userName={selectedUser.userName}
                onBack={() => setSelectedUser(null)}
            />
        )
    }

    const chartData = summary ? {
        labels: summary.dailyTrend.map(x => x.date.slice(5)),
        datasets: [{
            label: t("Acciones"),
            data: summary.dailyTrend.map(x => x.count),
            borderColor: "#22c55e",
            backgroundColor: "#22c55e20",
            fill: true,
            tension: 0.4,
            pointRadius: 2,
        }],
    } : null

    const chartOptions = {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    }

    const maxModuleCount = summary?.moduleBreakdown[0]?.count || 1
    const topUsers = summary?.topUsers || []

    return (
        <div>
            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button className="btn btn-sm btn-outline-secondary" onClick={onBack}>
                        <i className="fas fa-arrow-left me-1" />
                        {t("Volver")}
                    </button>
                    <div>
                        <h5 className="fw-bold mb-0">
                            <i className="fas fa-building me-2 text-success" />
                            {t("Detalle de Organización")}: {summary?.entityName || organizationName}
                        </h5>
                        {summary?.entityEmail && (
                            <small className="text-muted">{summary.entityEmail}</small>
                        )}
                    </div>
                </div>
                <div className="btn-group mt-2 mt-sm-0">
                    {[7, 14, 30, 60, 90].map(d => (
                        <button
                            key={d}
                            className={`btn btn-sm ${days === d ? "btn-success" : "btn-outline-success"}`}
                            onClick={() => { setDays(d); setPage(0) }}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {isLoadingSummary ? (
                <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-success" role="status" />
                </div>
            ) : summary ? (
                <>
                    {/* Summary cards */}
                    <div className="row mb-4">
                        {[
                            { icon: "fas fa-bolt", color: "#3b82f6", value: summary.totalActions.toLocaleString(), label: t("Total Acciones") },
                            { icon: "fas fa-users", color: "#8b5cf6", value: topUsers.length, label: t("Usuarios Activos") },
                            { icon: "fas fa-th-large", color: "#14b8a6", value: summary.modulesUsed, label: t("Módulos") },
                            { icon: "fas fa-calendar-check", color: "#22c55e", value: summary.firstActivity || "-", label: t("Primera Actividad") },
                        ].map((card, i) => (
                            <div key={i} className="col-sm-6 col-lg-3 mb-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body d-flex align-items-center gap-3 p-3">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 44, height: 44, background: card.color + "22" }}
                                        >
                                            <i className={`${card.icon} fa-lg`} style={{ color: card.color }} />
                                        </div>
                                        <div>
                                            <div className="fw-bold" style={{ fontSize: "1.3rem" }}>{card.value}</div>
                                            <div className="text-muted small">{card.label}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Chart + Module breakdown */}
                    <div className="row mb-4">
                        <div className="col-lg-7 mb-3 mb-lg-0">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="fas fa-chart-line me-2 text-info" />
                                        {t("Actividad Diaria")}
                                    </h6>
                                    {chartData && summary.dailyTrend.length > 0 ? (
                                        <Line data={chartData} options={chartOptions} />
                                    ) : (
                                        <p className="text-muted small">{t("Sin datos disponibles")}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="fas fa-th-large me-2 text-warning" />
                                        {t("Desglose por Módulo")}
                                    </h6>
                                    <div className="d-flex flex-column gap-2">
                                        {summary.moduleBreakdown.map((mod, i) => {
                                            const pct = (mod.count / maxModuleCount) * 100
                                            const color = moduleColors[i % moduleColors.length]
                                            return (
                                                <div key={mod.module}>
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span className="small fw-medium">{mod.module}</span>
                                                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                            {mod.count.toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="progress" style={{ height: 6 }}>
                                                        <div
                                                            className="progress-bar"
                                                            style={{ width: `${pct}%`, backgroundColor: color }}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {summary.moduleBreakdown.length === 0 && (
                                            <p className="text-muted small">{t("Sin datos disponibles")}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Users + Action breakdown */}
                    <div className="row mb-4">
                        <div className="col-lg-6 mb-3 mb-lg-0">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="fas fa-user me-2 text-primary" />
                                        {t("Top Usuarios")}
                                    </h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>{t("Usuario")}</th>
                                                    <th className="text-end">{t("Acciones")}</th>
                                                    <th className="text-end">{t("Módulos")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topUsers.map((u, i) => (
                                                    <tr
                                                        key={i}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setSelectedUser({ userId: u.userId, userName: u.userName })}
                                                    >
                                                        <td className="text-muted">{i + 1}</td>
                                                        <td className="fw-medium text-primary">{u.userName || u.userId.slice(0, 8)}</td>
                                                        <td className="text-end">{u.totalActions.toLocaleString()}</td>
                                                        <td className="text-end">{u.modules}</td>
                                                    </tr>
                                                ))}
                                                {topUsers.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} className="text-center text-muted py-3">
                                                            {t("Sin datos disponibles")}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="fas fa-tags me-2 text-secondary" />
                                        {t("Desglose por Acción")}
                                    </h6>
                                    {summary.actionBreakdown.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {summary.actionBreakdown.map(a => (
                                                <span key={a.action} className="badge bg-light text-dark px-3 py-2">
                                                    {a.action}: <strong>{a.count.toLocaleString()}</strong>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted small">{t("Sin datos disponibles")}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}

            {/* Activity log table */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                    <h6 className="fw-semibold mb-0">
                        <i className="fas fa-list me-2 text-success" />
                        {t("Activity Log")}
                        <span className="badge bg-light text-dark ms-2">{totalRecords.toLocaleString()}</span>
                    </h6>
                </div>
                <div className="card-body p-0">
                    {isLoadingLogs ? (
                        <div className="d-flex justify-content-center py-5">
                            <div className="spinner-border text-success" role="status" />
                        </div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t("No hay registros para los filtros actuales.")}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>{t("Fecha")}</th>
                                        <th>{t("Usuario")}</th>
                                        <th>{t("Módulo")}</th>
                                        <th>{t("Acción")}</th>
                                        <th>{t("Contenido")}</th>
                                        <th>IP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.date ? new Date(item.date).toLocaleString() : "-"}</td>
                                            <td
                                                className="fw-medium text-primary"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    if (item.userId) setSelectedUser({ userId: item.userId, userName: item.userName || item.userId })
                                                }}
                                            >
                                                {item.userName || item.userId || "-"}
                                            </td>
                                            <td>{item.section || "-"}</td>
                                            <td>{item.action || "-"}</td>
                                            <td><span className="text-muted">{item.content || item.itemId || "-"}</span></td>
                                            <td>{item.ipAddress || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="card-footer d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        {t("Página")} {totalPages === 0 ? 0 : page + 1} {t("de")} {totalPages}
                    </small>
                    <div className="btn-group">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={page <= 0}
                            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                        >
                            <i className="fas fa-chevron-left me-1" />
                            {t("Anterior")}
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={page >= totalPages - 1 || totalPages === 0}
                            onClick={() => setPage(prev => prev + 1)}
                        >
                            {t("Siguiente")}
                            <i className="fas fa-chevron-right ms-1" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminActivityLogOrgDetail
